/**
 * The Quote's optional lines (decision 2650; T1247). A Quote line the customer
 * may take or leave carries `isOptional`; the Quote's total is its required
 * lines and the optional lines the acceptance took, and before an answer no
 * optional line is taken. The approval gate and the document floor measure
 * every line, the most the customer can take, so they read `allCents`.
 * DOM-free, so the API, the page and the public link read one rule.
 */

/** A line as the totals read it: its own money after its discount, and the tax it carries, in cents. */
export interface OptionLineAmounts {
  id: string;
  isOptional: boolean;
  amountCents: number;
  taxCents: number;
}

/** The Quote's totals split by its optional lines, each net of tax beside the tax it carries. */
export interface QuoteOptionTotals {
  /** Whether any line is optional; a Quote with none prints and reads as it always did. */
  hasOptional: boolean;
  requiredCents: number;
  requiredTaxCents: number;
  optionalCents: number;
  optionalTaxCents: number;
  /** The required lines and the optional lines taken: the Quote's total. */
  takenCents: number;
  takenTaxCents: number;
  /** Every line, optional ones included: the most the customer can take. */
  allCents: number;
  allTaxCents: number;
}

/** 🔒 Whether a line counts in the Quote's total: every required line, and an optional one only where it was taken. */
export function countsInQuoteTotal(line: { id: string; isOptional: boolean }, taken: Iterable<string> | null | undefined): boolean {
  if (!line.isOptional) return true;
  if (!taken) return false;
  for (const id of taken) if (id === line.id) return true;
  return false;
}

/** 🔒 The split totals (decision 2650). `taken` is the acceptance's optional lines, or null where nothing was accepted. */
export function quoteOptionTotals(lines: readonly OptionLineAmounts[], taken: readonly string[] | null): QuoteOptionTotals {
  const takenSet = new Set(taken ?? []);
  const totals: QuoteOptionTotals = { hasOptional: false, requiredCents: 0, requiredTaxCents: 0, optionalCents: 0, optionalTaxCents: 0, takenCents: 0, takenTaxCents: 0, allCents: 0, allTaxCents: 0 };
  for (const line of lines) {
    totals.allCents += line.amountCents;
    totals.allTaxCents += line.taxCents;
    if (line.isOptional) {
      totals.hasOptional = true;
      totals.optionalCents += line.amountCents;
      totals.optionalTaxCents += line.taxCents;
    } else {
      totals.requiredCents += line.amountCents;
      totals.requiredTaxCents += line.taxCents;
    }
    if (!line.isOptional || takenSet.has(line.id)) {
      totals.takenCents += line.amountCents;
      totals.takenTaxCents += line.taxCents;
    }
  }
  return totals;
}

/**
 * 🔒 The optional lines an answer names, checked against the Quote's own
 * (decision 2650): a list of ids, each one of the Quote's optional lines, none
 * twice. Null where the list holds; the sentence where it does not.
 */
export function takenOptionalProblem(ids: unknown, optionalLineIds: readonly string[]): string | null {
  if (!Array.isArray(ids)) return 'The optional lines taken are sent as a list.';
  const allowed = new Set(optionalLineIds);
  const seen = new Set<string>();
  for (const id of ids) {
    if (typeof id !== 'string' || !allowed.has(id)) return 'An optional line taken is one of this quote’s own optional lines.';
    if (seen.has(id)) return 'An optional line is taken once.';
    seen.add(id);
  }
  return null;
}
